import { App, Button, Flex, theme, Typography, Upload } from 'antd'
import type { RcFile } from 'antd/es/upload'
import { ImageUp, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { IconWithBackground } from 'src/components/IconWithBackground'
import { useIconSizes } from 'src/hooks'
import {
  isSvgFile,
  MAX_SVG_MB,
  storableFile,
  SVG_ACCEPT,
  SVG_MIME
} from 'src/utils/svgUpload'

import { useStyles } from './styles'
import type { IconPickerPhotoConfig, PickedPhoto } from './types'

const { Text } = Typography

const DEFAULT_ACCEPT = `image/png,image/jpeg,${SVG_ACCEPT}`
const BYTES_IN_MB = 1024 * 1024
const DEFAULT_MAX_SIZE_BYTES = BYTES_IN_MB
const DEFAULT_MIN_PIXELS = 128
const BADGE_SIZE = 44

const readImageSize = (file: RcFile) =>
  new Promise<{ width: number; height: number }>((resolve) => {
    const url = URL.createObjectURL(file)
    const image = new Image()

    const finish = (width: number, height: number) => {
      URL.revokeObjectURL(url)
      resolve({ width, height })
    }

    image.onload = () => finish(image.naturalWidth, image.naturalHeight)
    image.onerror = () => finish(0, 0)
    image.src = url
  })

type IconPickerPhotoPanelProps = {
  config: IconPickerPhotoConfig
  value: PickedPhoto | null
  onChange: (photo: PickedPhoto | null) => void
}

export const IconPickerPhotoPanel = ({
  config,
  value,
  onChange
}: IconPickerPhotoPanelProps) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { token } = theme.useToken()
  const { message } = App.useApp()
  const iconSizes = useIconSizes()

  const accept = config.accept ?? DEFAULT_ACCEPT
  const maxSizeBytes = config.maxSizeBytes ?? DEFAULT_MAX_SIZE_BYTES
  const minPixels = config.minPixels ?? DEFAULT_MIN_PIXELS
  const maxSizeMb = Math.round((maxSizeBytes / BYTES_IN_MB) * 10) / 10

  const handleBeforeUpload = async (file: RcFile) => {
    const isSvg = isSvgFile(file)

    if (!accept.split(',').includes(isSvg ? SVG_MIME : file.type)) {
      message.error(t('iconPicker.photoTypeError'))

      return Upload.LIST_IGNORE
    }

    if (file.size > maxSizeBytes) {
      message.error(t('iconPicker.photoSizeError', { size: maxSizeMb }))

      return Upload.LIST_IGNORE
    }

    const stored = await storableFile(file)

    if (typeof stored === 'string') {
      message.error(t(`svgUpload.${stored}`, { size: MAX_SVG_MB }))

      return Upload.LIST_IGNORE
    }

    // A vector has no pixel size to fall short of: it is drawn at any size.
    if (!isSvg) {
      const { width, height } = await readImageSize(file)

      if (width && (width < minPixels || height < minPixels)) {
        message.error(t('iconPicker.photoDimensionError', { size: minPixels }))

        return Upload.LIST_IGNORE
      }
    }

    // The preview is the copy that will be stored, so an SVG shows as sanitized.
    onChange({
      kind: 'photo',
      source: 'file',
      url: URL.createObjectURL(stored),
      file
    })

    return Upload.LIST_IGNORE
  }

  return (
    <Flex vertical gap={12}>
      <Upload.Dragger
        accept={accept}
        maxCount={1}
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
      >
        <Flex vertical align="center" gap={8} className={styles.dropZone}>
          {value ? (
            <img className={styles.photoPreview} src={value.url} alt="" />
          ) : (
            <IconWithBackground
              size={BADGE_SIZE}
              shape="square"
              color={token.colorPrimary}
            >
              <ImageUp />
            </IconWithBackground>
          )}

          <Text>
            {value ? t('iconPicker.photoReplace') : t('iconPicker.photoDrop')}{' '}
            <Text strong style={{ color: token.colorPrimary }}>
              {t('iconPicker.photoDropAction')}
            </Text>
          </Text>

          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('iconPicker.photoHint', {
              size: maxSizeMb,
              pixels: minPixels
            })}
          </Text>
        </Flex>
      </Upload.Dragger>

      {value && (
        <Flex justify="center">
          <Button
            icon={<Trash2 size={iconSizes.small} />}
            onClick={() => onChange(null)}
          >
            {t('iconPicker.photoRemove')}
          </Button>
        </Flex>
      )}
    </Flex>
  )
}
